import { useEffect, useMemo, useRef, useState } from "react"
import { Box, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { DateTime } from "luxon"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { faFilePdf, faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { useAppLocalization } from "../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../core/api/components/ApiQueryTable"
import { GROW_CELL_CLASS, useGridLayoutStyles } from "../../../../shared/components/table/useGridLayoutStyles"
import { COLUMN_SIZE } from "../../../../shared/constants/table/config"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import {
    MeetingServiceOrdersForOfficeManagerResponse,
    MerchantsForOfficeManagerResponse,
    fetchMeetingServiceOrdersForOfficeManager,
    fetchMerchantsForOfficeManager,
} from "../meetingServiceApi"
import { useAppSelector } from "../../../store/storeHooks"
import { selectEffectiveCompanyId, selectEffectiveUserId } from "../../../context/contextSelectors"
import { selectUserGroupId } from "../../../login/loginSelectors"
import { MeetingServiceOrder } from "../meetingServiceTypes"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../shared/components/links/StandardBreadcrumbs"
import { TENANT_REPORTS_ROUTE } from "../../../../constants/routes"
import { ActionBarItem, FloatingActionBar } from "../../../../shared/components/table/FloatingActionBar"
import { StandardTableFilter } from "../../../../shared/components/table/StandardTableFilters"
import { StandardDateRangeFilter } from "../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../shared/components/table/useInitialDateFilters"
import { MeetingServiceOrderDrawer } from "../../../../shared/components/meetingServiceOrderDrawer/MeetingServiceOrderDrawer"
import { isOrderRowControlClick } from "../../../../shared/components/meetingServiceOrderDrawer/orderDrawerRow"
import { ChatMessageAuthorRole, UserType } from "../../../../shared/components/orderChat/orderChatTypes"

const useStyles = makeStyles()(() => ({
    table: {
        ".MuiTableCell-root": {
            paddingLeft: "1rem",
            paddingRight: "1rem",
        },
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
    dateFilter: {
        maxWidth: "11rem",
    },
}))

const selectMeetingService = (data: MeetingServiceOrdersForOfficeManagerResponse) => data?.orders || []
const selectMerchants = (data: MerchantsForOfficeManagerResponse) => data?.merchants || []

export function MeetingService() {
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const userId = useAppSelector(selectEffectiveUserId)
    const userGroupId = useAppSelector(selectUserGroupId)
    const { classes } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()
    const { l10n } = useAppLocalization()

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate,
        endDate,
    })
    const [merchantId, setMerchantId] = useState<string>("")
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()
    const [drawerOrder, setDrawerOrder] = useState<MeetingServiceOrder | undefined>(undefined)

    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)
    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const meetingServiceOrdersResult = useApiQuery(fetchMeetingServiceOrdersForOfficeManager, {
        queryName: "meeting-service-orders-for-office-manager",
        dependencies: {
            companyId: companyId ?? 0,
            userGroupId: userGroupId ?? 0,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
            merchantId: merchantId,
        },
        enabled: Boolean(companyId) && Boolean(userGroupId) && Boolean(merchantId),
        select: selectMeetingService,
    })

    const merchantsForOfficeManagerResult = useApiQuery(fetchMerchantsForOfficeManager, {
        queryName: "merchants-for-office-manager",
        dependencies: {
            companyId: companyId ?? 0,
            userGroupId: userGroupId ?? 0,
        },
        enabled: Boolean(companyId) && Boolean(userGroupId),
        select: selectMerchants,
    })

    useEffect(() => {
        if (merchantsForOfficeManagerResult.data && merchantsForOfficeManagerResult.data?.length > 0) {
            setMerchantId(merchantsForOfficeManagerResult.data[0].id.toString())
        }
    }, [merchantsForOfficeManagerResult.data])

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceOrdersResult)

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("office-meeting-service-table-order-id-column"),
                accessorKey: "id",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("office-meeting-service-table-booked-date-column"),
                    accessorKey: "accountingDetails.bookedTime",
                },
                (row) => row?.accountingDetails?.bookedTime,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("office-meeting-service-table-delivery-time-column"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row?.deliveryDetails?.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("office-meeting-service-table-creator-column"),
                accessorKey: "customer.displayName",
                size: COLUMN_SIZE.XL,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
                Cell: ({ row }) => {
                    return (
                        <Grid>
                            <span>
                                {row.original.customer.initials}, {row.original.customer.displayName}
                            </span>
                            <span>{row.original.meetingDetails.meetingName}</span>
                        </Grid>
                    )
                },
            }),
            columnFactory.moneyColumn(
                { header: l10n.getString("office-meeting-service-table-amount-column"), id: "amount" },
                (row) => row.amount,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }
    const handleChangeMerchant = (event: SelectChangeEvent) => {
        setMerchantId(event.target.value)
    }

    return (
        <>
            <LocalizedStrict id="office-meeting-service-header" attrs={{ title: true }}>
                <PageHeader page={Page.TENANT_MEETING_SERVICE} title="Meeting Service">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="tenant-nav-item-home">
                            <BreadcrumbLink to={TENANT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="tenant-nav-item-accounting-header">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="office-meeting-service-title">
                            <Breadcrumb>Meeting Service</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Box className={classes.table}>
                <ApiQueryTable
                    layoutMode="grid"
                    muiTablePaperProps={{ className: gridClasses.pinned }}
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    muiTableBodyRowProps={({ row }) => ({
                        sx: { cursor: "pointer" },
                        onClick: (event) => {
                            if (isOrderRowControlClick(event.target)) return
                            setDrawerOrder(row.original)
                        },
                    })}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                                // handleShowReceipt(row.original.id)
                            }}
                            key={`show-receipt-${row.original.id}`}
                        >
                            {l10n.getString("office-meeting-service-table-show-receipt")}
                        </MenuItem>,
                    ]}
                    renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <StandardDateRangeFilter
                                inputClasses={classes.dateFilter}
                                value={dateState}
                                onChange={setDateState}
                            />
                            <FormControl fullWidth className={classes.dateFilter}>
                                <InputLabel id="merchant-select-label">
                                    <LocalizedStrict id="office-meeting-service-table-merchant">
                                        <>Merchant</>
                                    </LocalizedStrict>
                                </InputLabel>
                                <Select
                                    labelId="merchant-select-label"
                                    id="merchant-select"
                                    value={merchantId}
                                    label="Merchant"
                                    onChange={handleChangeMerchant}
                                >
                                    {!!merchantsForOfficeManagerResult.data?.length &&
                                        merchantsForOfficeManagerResult.data.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </StandardTableFilter>
                    )}
                    state={{
                        rowSelection,
                    }}
                />
            </Box>
            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict
                        id="office-meeting-service-table-floating-action-bar-receipts"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem icon={faFilePdf} title="Receipts" onClick={() => {}} />
                    </LocalizedStrict>

                    <LocalizedStrict
                        id="office-meeting-service-table-floating-action-bar-export"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem icon={faFileArrowDown} title="Export" onClick={() => {}} />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}

            {drawerOrder && (
                <MeetingServiceOrderDrawer
                    open
                    order={drawerOrder}
                    viewerRole={ChatMessageAuthorRole.CUSTOMER}
                    sender={userId && companyId ? { type: UserType.USER, companyId, userId } : undefined}
                    onClose={() => setDrawerOrder(undefined)}
                />
            )}
        </>
    )
}
